import React, { useState } from 'react';
import { NumberInput } from '../primitives/input';
import { Button } from '~/primitives/button';
import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
} from '../primitives/dialog';
import { DialogClose } from '@radix-ui/react-dialog';
import { CompletionTime } from '~/util/types/submissions';
import {
    cn,
    completionTimeSplit,
    completionTimeToMs,
    getCompletionTime,
} from '~/util';

export const EditableCompletionTimeField: React.FC<{
    completionTime: number;
    setCompletionTime: (time: number) => void;
    disabled?: boolean;
}> = ({ completionTime, setCompletionTime, disabled }) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const [splitCompletionTime, setSplitCompletionTime] =
        useState<CompletionTime>(() => completionTimeSplit(completionTime));

    const handleSave = () => {
        setCompletionTime(completionTimeToMs(splitCompletionTime));
        setModalOpen(false);
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={setModalOpen}>
            <DialogTrigger asChild disabled={disabled}>
                <div
                    className={cn(
                        'rounded-md bg-background-light p-4 text-lg ring-offset-background hover:cursor-pointer hover:bg-background-semilight transition-colors placeholder:text-secondary-light disabled:cursor-not-allowed disabled:opacity-50',
                        disabled && 'cursor-not-allowed opacity-50',
                    )}
                >
                    <p
                        className={
                            !completionTime || completionTime == 0
                                ? 'text-secondary-light'
                                : 'text-secondary-foreground'
                        }
                    >
                        {getCompletionTime(completionTime)}
                    </p>
                </div>
            </DialogTrigger>
            <DialogContent className="w-max" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>Set completion time</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex flex-col">
                        <label htmlFor="hours">Hours:</label>
                        <NumberInput
                            className="lg:max-w-40 bg-background-dark hover:bg-background-light"
                            id="hours"
                            placeholder="00"
                            padZeroStart={2}
                            value={splitCompletionTime.hours}
                            onChange={(value) =>
                                setSplitCompletionTime({
                                    ...splitCompletionTime,
                                    hours: value ?? 0,
                                })
                            }
                        />
                    </div>
                    <div className="mt-auto pb-4 hidden lg:block">:</div>
                    <div className="flex flex-col">
                        <label htmlFor="minutes">Minutes:</label>
                        <NumberInput
                            className="lg:max-w-40 bg-background-dark hover:bg-background-light"
                            id="minutes"
                            placeholder="00"
                            padZeroStart={2}
                            value={splitCompletionTime.minutes}
                            onChange={(value) => {
                                const parsed = value ?? 0;
                                setSplitCompletionTime({
                                    ...splitCompletionTime,
                                    minutes: parsed > 59 ? 59 : parsed,
                                });
                            }}
                        />
                    </div>
                    <div className="mt-auto pb-4 hidden lg:block">:</div>
                    <div className="flex flex-col">
                        <label htmlFor="seconds">Seconds:</label>
                        <NumberInput
                            className="lg:max-w-40 bg-background-dark hover:bg-background-light"
                            id="seconds"
                            placeholder="00"
                            padZeroStart={2}
                            value={splitCompletionTime.seconds}
                            onChange={(value) => {
                                const parsed = value ?? 0;
                                setSplitCompletionTime({
                                    ...splitCompletionTime,
                                    seconds: parsed > 59 ? 59 : parsed,
                                });
                            }}
                        />
                    </div>
                    <div className="mt-auto pb-4 hidden lg:block">.</div>
                    <div className="flex flex-col">
                        <label htmlFor="milliseconds">Milliseconds:</label>
                        <NumberInput
                            className="lg:max-w-40 bg-background-dark hover:bg-background-light"
                            id="milliseconds"
                            placeholder="000"
                            padZeroStart={3}
                            value={splitCompletionTime.milliseconds}
                            onChange={(value) => {
                                const parsed = value ?? 0;
                                setSplitCompletionTime({
                                    ...splitCompletionTime,
                                    milliseconds: parsed > 999 ? 999 : parsed,
                                });
                            }}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="secondary">Cancel</Button>
                    </DialogClose>
                    <DialogClose asChild>
                        <Button onClick={handleSave}>Save</Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
